import { Avatar, AvatarFallback, AvatarImage } from '@/shared/components/ui/avatar';
import { Card } from '@/shared/components/ui/card';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/shared/components/ui/tooltip';
import type { Message } from '../../types';
import { formatMessageTime } from './formatMessageTime';
import { getInitials } from './getInitials';

type MessageBubbleProps = {
  message: Message;
  isOwnMessage: boolean;
  showAvatar?: boolean;
  participantName: string;
  participantPhoto: string | null;
  /** Show the sender-name caption (first message of a sender/performer run). */
  showSenderName?: boolean;
  /** Logged-in user's own id — distinguishes "my" sends from a teammate's. */
  currentUserId?: string;
};

export const MessageBubble = ({
  message,
  isOwnMessage,
  showAvatar,
  participantName,
  participantPhoto,
  showSenderName,
  currentUserId,
}: MessageBubbleProps) => {
  if (typeof message.content !== 'string' || !message.content.trim()) {
    return null;
  }

  // When a sub-delegate typed the message, `performedBy` is their profile —
  // the actual sender, even though the conversation `senderId` is the owner.
  const performer = message.performedBy ?? null;

  // Incoming side: render the delegate (performer) as the sender when present,
  // otherwise the owner participant — keeps 1:1 chats byte-identical.
  const incomingName = performer ? performer.name : participantName;
  const incomingPhoto = performer ? performer.profilePhoto : participantPhoto;

  // Own side: a teammate (not me) sent this on the shared workspace inbox.
  const ownTypedByTeammate = Boolean(
    isOwnMessage && performer && performer.id !== currentUserId,
  );

  const attribution = !isOwnMessage
    ? performer
      ? performer.name
      : null
    : ownTypedByTeammate
      ? `Sent by ${performer!.name}`
      : null;

  // Hover tooltip with the real sender — only meaningful when a delegate typed.
  const tooltipText = performer
    ? isOwnMessage
      ? `Sent by ${performer.name}`
      : performer.name
    : null;

  const bubble = (
    <Card
      className={`
        p-3 
        ${isOwnMessage ? 'bg-[#026857] text-white' : 'bg-gray-100 text-gray-900'}
      `}
    >
      <p className="text-sm whitespace-pre-wrap break-words">{message.content}</p>
    </Card>
  );

  return (
    <div
      className={`flex gap-3 mb-4 ${isOwnMessage ? 'flex-row-reverse' : 'flex-row'}`}
      data-message-id={message.id}
    >
      {/* Avatar - only show for incoming messages (sender = participant or delegate) */}
      {!isOwnMessage && showAvatar && (
        <Avatar className="h-8 w-8 flex-shrink-0">
          <AvatarImage src={incomingPhoto || undefined} alt={incomingName} />
          <AvatarFallback className="bg-[#026857] text-white text-xs">
            {getInitials(incomingName)}
          </AvatarFallback>
        </Avatar>
      )}

      {/* Spacer for messages without avatar */}
      {!isOwnMessage && !showAvatar && <div className="w-8 flex-shrink-0" />}

      {/* Message Content */}
      <div className={`flex flex-col ${isOwnMessage ? 'items-end' : 'items-start'} max-w-[70%]`}>
        {attribution && showSenderName && (
          <span className="text-xs text-gray-500 mb-1 px-1" data-testid="message-attribution">
            {attribution}
          </span>
        )}

        {tooltipText ? (
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>{bubble}</TooltipTrigger>
              <TooltipContent>{tooltipText}</TooltipContent>
            </Tooltip>
          </TooltipProvider>
        ) : (
          bubble
        )}

        {/* Timestamp and Status */}
        <div className="flex items-center gap-2 mt-1 px-1">
          <span className="text-xs text-gray-500">{formatMessageTime(message.createdAt)}</span>
          {isOwnMessage && message.readAt && <span className="text-xs text-[#026857]">Read</span>}
        </div>
      </div>
    </div>
  );
};
